/**
 * Phase 6e.3 — overwrite confirmation modal for the Magic Button.
 *
 * Shown only when `detectOverwriteCount(engagement) > 0`. Reuses the
 * shadcn Dialog primitive already in the codebase. ESC + the cancel
 * button both trigger `onCancel`; clicking the destructive confirm
 * triggers `onConfirm`.
 *
 * Component is purely presentational — does not call the sync, does
 * not touch the store. The Magic Button owns the click flow.
 */

import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface FxSyncOverwriteModalProps {
  open: boolean;
  overwriteCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}

export function FxSyncOverwriteModal({
  open,
  overwriteCount,
  onConfirm,
  onCancel,
}: FxSyncOverwriteModalProps) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent data-testid="fx-sync-overwrite-modal">
        <DialogHeader>
          <DialogTitle>{t("fx.sync.overwrite.title")}</DialogTitle>
          <DialogDescription>
            {t("fx.sync.overwrite.body", { count: overwriteCount })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            data-testid="fx-sync-overwrite-cancel"
          >
            {t("fx.sync.overwrite.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            data-testid="fx-sync-overwrite-confirm"
          >
            {t("fx.sync.overwrite.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
